import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Animated,
  Dimensions,
  Easing,
  FlatList,
  Keyboard,
  Platform,
  TouchableWithoutFeedback,
  View,
} from 'react-native';

import LessonIndexHeader from './lesson-index-header/lesson-index-header';
import { LessonIndexItem } from './lesson-index-item/lesson-index-item';
import styles from './lesson-index.style';
import { LessonSlideStatus } from './lesson-slide-status.enum';
import { useColors, useLessonContext } from '../../hooks/lesson-player.context';
import { useKeyPressWithHandler } from '../../hooks/use-key-press-with-handler';

interface LessonIndexProps {
  slideNumber: number;
  completionList: boolean[];
  slides: any[];
  changeSlide: (index) => void;
  isVisible: boolean;
  title?: string;
  subtitle?: string;
  isFullScreen?: boolean;
}

const INDEX_WIDTH = 450;

export function LessonIndex({
  slideNumber = 0,
  completionList = [],
  slides,
  changeSlide: _changeSlide,
  isVisible = false,
  title,
  subtitle,
  isFullScreen = true,
}: LessonIndexProps) {
  const colors = useColors();
  const [visibility] = useState(new Animated.Value(0));
  const [inScreen, setInScreen] = useState(false);
  const { hideIndex } = useLessonContext();
  const screenWidth = Dimensions.get('window').width;
  const nextSlideIndex = useMemo(
    () => completionList.findIndex((isComplete) => !isComplete),
    [completionList]
  );

  const slidesWithStatus = useMemo(() => {
    return slides.map((slide, index) => {
      let status = LessonSlideStatus.LOCKED;
      if (index < nextSlideIndex || nextSlideIndex === -1) {
        status = LessonSlideStatus.COMPLETED;
      } else if (index === nextSlideIndex) {
        status = LessonSlideStatus.NEXT;
      }
      return { ...slide, status };
    });
  }, [slides, nextSlideIndex]);

  useEffect(() => {
    const toValue = isVisible ? 1 : 0;
    const duration = screenWidth > 767 ? 500 : 350;

    if (isVisible) {
      setInScreen(true);
    }

    Animated.timing(visibility, {
      toValue,
      duration,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      if (!isVisible) {
        setInScreen(false);
      }
    });

    Keyboard.dismiss();
  }, [isVisible, visibility, screenWidth]);

  function changeSlide(index) {
    if (index > nextSlideIndex && nextSlideIndex !== -1) {
      return;
    }

    _changeSlide(index);
  }

  const width = useMemo(() => {
    const webWidth = isFullScreen ? screenWidth : INDEX_WIDTH;
    if (Platform.OS === 'android' || Platform.OS === 'ios') {
      return screenWidth;
    } else if (screenWidth < webWidth) {
      return screenWidth;
    }
    return webWidth;
  }, [screenWidth, isFullScreen]);

  const keyPressHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.type !== 'keydown') {
        return;
      }

      if (event.key === 'Escape') {
        hideIndex();
      }
    },
    [hideIndex]
  );

  useKeyPressWithHandler(keyPressHandler);

  const translateX = visibility.interpolate({
    inputRange: [0, 1],
    outputRange: [screenWidth, screenWidth - width],
    extrapolate: 'clamp',
  });

  if (!inScreen) {
    return null;
  }

  return (
    <>
      {isVisible && (
        <TouchableWithoutFeedback onPress={hideIndex}>
          <View
            style={[
              {
                backgroundColor: colors.gray,
              },
              styles.overlay,
              styles.container,
            ]}
          />
        </TouchableWithoutFeedback>
      )}
      <Animated.View
        style={[
          {
            backgroundColor: colors.background,
            transform: [{ translateX }],
            width,
          },
          styles.container,
        ]}
      >
        <LessonIndexHeader title={title} subtitle={subtitle} />
        <FlatList
          data={slidesWithStatus}
          keyExtractor={(_item, index) => index.toString()}
          extraData={slideNumber}
          contentContainerStyle={styles.list}
          renderItem={({ item, index }) => {
            return (
              <LessonIndexItem
                index={index}
                text={item.name}
                status={item.status}
                isCurrent={slideNumber === index}
                onPress={() => changeSlide(index)}
              />
            );
          }}
        />
      </Animated.View>
    </>
  );
}
